import { Button, message, Modal, Popconfirm, Space, Table, Typography } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import styled from "styled-components";
import moment from "moment-timezone";
import {
  useDeleteDebtorReminderMutation,
  useFetchDebtorRemindersQuery,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { camelToWords, capitalizeFirstLetter } from "common/utils";
import { useFetchUserSummariesQuery } from "features/userManagementTable/userManagementAPI";
import { useParams } from "react-router-dom";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { DATE_TIME_FORMAT } from "common/constants";
import DebtorCreateReminderModal from "./debtorCreateReminderModal";

const { Text, Title } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 16px;
  }
`;

const StyledButtonContainer = styled.div`
  margin-bottom: 16px;
  text-align: right;
  position: absolute;
  top: 16px;
  left: 100px;
  z-index: 1;
`;

function DebtorRemindersTableModal({ open, onOk, onCancel }) {
  const { debtorId } = useParams();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  const { data: reminders = [], isLoading: isRemindersLoading } = useFetchDebtorRemindersQuery({
    debtorIds: [debtorId],
  });
  const {
    data: {
      [PERMISSIONS.USER_TASK__GET]: isUserTaskGetAuthorized,
      [PERMISSIONS.USER_TASK__CREATE]: isUserTaskCreateAuthorized,
      [PERMISSIONS.USER_TASK__DELETE]: isUserTaskDeleteAuthorized,
      [PERMISSIONS.USER_TASK__UPDATE]: isUserTaskUpdateAuthorized,
    },
  } = useAuthorizations();
  const [deleteReminder, { isLoading: isDeleteReminderLoading }] =
    useDeleteDebtorReminderMutation();

  const { data: users, isLoading: isUsersLoading } = useFetchUserSummariesQuery({
    isActive: true,
  });

  const handleCreateReminder = () => {
    setIsCreateModalVisible(true);
    setActiveRecord(null);
  };

  const handleEditReminder = (reminderId) => {
    setIsCreateModalVisible(true);
    setActiveRecord(reminders.find((reminder) => reminder.id === reminderId));
  };

  const handleDeleteReminder = async (reminderId) => {
    const result = await deleteReminder({ userTaskIds: [reminderId] });
    if ("data" in result) {
      message.success("Reminder deleted successfully!");
      setActiveRecord(null);
    }
  };

  const handleCreateModalOk = async (reminderData) => {
    setIsCreateModalVisible(false);
  };

  const handleCreateModalCancel = () => {
    setIsCreateModalVisible(false);
  };

  const columns = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => <Text>{moment(date).format(DATE_TIME_FORMAT)}</Text>,
      sorter: (a, b) => moment(a.createdAt).diff(moment(b.createdAt)),
    },
    {
      title: "Reminder",
      dataIndex: "message",
      key: "message",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Reminder Date",
      dataIndex: "remindAt",
      key: "remindAt",
      render: (date) => <Text>{moment(date).format(DATE_TIME_FORMAT)}</Text>,
      sorter: (a, b) => moment(a.remindAt).diff(moment(b.remindAt)),
    },
    {
      title: "Assigned To",
      dataIndex: "assigneeId",
      key: "assigneeId",
      render: (assigneeId) => <Text>{users?.find((user) => user.id === assigneeId)?.name}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Text type={status === "completed" ? "success" : "warning"}>
          {capitalizeFirstLetter(camelToWords(status))}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "action",
      render: (_, record) => (
        <Space>
          {isUserTaskUpdateAuthorized && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditReminder(record.id)}
            />
          )}
          {isUserTaskDeleteAuthorized && (
            <Popconfirm
              title="Are you sure you want to delete this reminder?"
              onConfirm={() => handleDeleteReminder(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="link"
                loading={isDeleteReminderLoading}
                disabled={isDeleteReminderLoading}
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <StyledModal
        title="Reminders"
        open={open}
        onCancel={onCancel}
        footer={null}
        width={800}
        maskClosable={false}
      >
        {isUserTaskCreateAuthorized && (
          <StyledButtonContainer>
            <Button type="link" icon={<PlusOutlined />} onClick={handleCreateReminder}>
              Add Reminder
            </Button>
          </StyledButtonContainer>
        )}
        {isUserTaskGetAuthorized && (
          <Table
            dataSource={reminders}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} reminders`,
            }}
            size="small"
            locale={{
              emptyText: "No reminders found",
            }}
          />
        )}
      </StyledModal>

      <DebtorCreateReminderModal
        reminder={activeRecord}
        open={isCreateModalVisible}
        onOk={handleCreateModalOk}
        onCancel={handleCreateModalCancel}
      />
    </>
  );
}

export default DebtorRemindersTableModal;
